import { useEffect, useId, useState, type ReactNode } from 'react';
import { CalendarClock, ChevronDown, CircleHelp, Mail, MessageSquare, Send, Users } from 'lucide-react';
import { STAENDE, STAND_INFO, type NaechsterSchritt, type SchrittArt } from '../../lib/planung';
import { Avatar } from './Avatar';
import { StandBadge } from './PlanungsChips';
import { SCHRITT_INFO } from './schrittInfo';
import './Planung.css';

const MERKEN = 'gm-legende-offen';

function gemerkt(): boolean {
  try { return localStorage.getItem(MERKEN) === '1'; } catch { return false; }
}

/** Kleines Kalenderblatt als Muster (gleiche Klassen wie auf der Karte). */
function Blatt({ art, children }: { art: string; children?: ReactNode }) {
  return (
    <span className={`gm-kachel gm-kachel--mini gm-kachel--${art}`} aria-hidden="true">
      {children ?? <><span className="gm-kachel-wt">Do</span><span className="gm-kachel-tag">8</span></>}
    </span>
  );
}

function Eintrag({ muster, titel, children }: { muster: ReactNode; titel: string; children: ReactNode }) {
  return (
    <li className="gm-legende-eintrag">
      <span className="gm-legende-muster">{muster}</span>
      <span className="gm-legende-text">{titel && <><strong>{titel}</strong> </>}{children}</span>
    </li>
  );
}

const SCHRITT_ERKLAERT: { art: SchrittArt; ton: NaechsterSchritt['ton']; text: string }[] = [
  { art: 'versandbereit', ton: 'erfolg', text: 'Abgestimmt, nichts offen, Empfänger da. Es fehlt nur noch der Schritt in Brevo: einplanen oder — wenn angehalten — fortsetzen.' },
  { art: 'erledigt', ton: 'erfolg', text: 'Versendet oder in Brevo eingeplant — die Mail geht von selbst raus.' },
  { art: 'handeln', ton: 'warnung', text: 'Du bist dran: neue Rückmeldungen lesen, offene Punkte einarbeiten, Empfänger wählen oder den Stand setzen.' },
  { art: 'termin', ton: 'warnung', text: 'Versandtag fehlt oder ist schon vorbei.' },
  { art: 'brevo', ton: 'warnung', text: 'In Brevo liegt noch ein älterer Stand — erst übertragen, dann einplanen.' },
  { art: 'warten', ton: 'info', text: 'Wir warten auf andere: auf Antworten der Gefragten oder auf die Freigabe.' },
];

/**
 * „Was bedeuten Farben und Rahmen?" — aufklappbar, gemerkt je Browser.
 * Erklärt Kalenderblatt, Karte, nächsten Schritt, Stand und Etiketten mit
 * denselben Bausteinen, die in der Liste stehen.
 */
export function PlanungsLegende() {
  const [offen, setOffen] = useState(gemerkt);
  const id = useId();
  useEffect(() => {
    try { localStorage.setItem(MERKEN, offen ? '1' : '0'); } catch { /* ohne Speicher bleibt sie zu */ }
  }, [offen]);

  return (
    <section className={`gm-legende${offen ? ' gm-legende--offen' : ''}`}>
      <h2 className="gm-legende-kopf">
        <button type="button" className="gm-legende-knopf" aria-expanded={offen} aria-controls={id} onClick={() => setOffen((o) => !o)}>
          <CircleHelp size={15} aria-hidden="true" />
          Was bedeuten Farben und Rahmen?
          <ChevronDown size={15} aria-hidden="true" className="gm-legende-pfeil" />
        </button>
      </h2>
      <div id={id} className="gm-legende-inhalt" hidden={!offen}>
        <p className="gm-legende-regel">
          <strong>Faustregel:</strong> Grün = fertig oder versandbereit · Orange = Du bist dran oder etwas hängt ·
          Blau = wir warten auf andere · Grau = noch nichts passiert · Gestrichelt = noch nicht fest.
          Neben jeder Farbe steht immer auch ein Wort oder Zeichen.
        </p>

        <div className="gm-legende-raster">
          <div className="gm-legende-block">
            <h3>Kalenderblatt links (Versandtag)</h3>
            <ul>
              <Eintrag muster={<Blatt art="eingeplant" />} titel="Ausgefüllt dunkelrot:">In Brevo eingeplant — geht an dem Tag von selbst raus.</Eintrag>
              <Eintrag muster={<Blatt art="geplant" />} titel="Durchgezogener Rahmen:">Versandtag ist unter „Termine“ eingetragen, in Brevo aber noch nicht eingeplant.</Eintrag>
              <Eintrag muster={<Blatt art="aus_name" />} titel="Gestrichelter Rahmen:">Der Tag ist nur abgeleitet — aus dem Mailnamen („laut Name“) oder als Wunschtermin aus dem Assistenten („Wunsch“). Fest wird er unter „Termine“ oder in Brevo.</Eintrag>
              <Eintrag muster={<Blatt art="angehalten" />} titel="Orange:">In Brevo angehalten — geht erst raus, wenn sie dort fortgesetzt wird. Es gilt der unter „Termine“ eingetragene Tag.</Eintrag>
              <Eintrag muster={<Blatt art="versendet" />} titel="Grau:">Schon versendet.</Eintrag>
              <Eintrag muster={<Blatt art="ohne"><CalendarClock size={14} /></Blatt>} titel="Leer, gestrichelt:">Noch kein Versandtermin.</Eintrag>
              <Eintrag muster={<Blatt art="event" />} titel="Hellrot (bei „Nach Event“):">Zeigt den Tag des Events statt des Versandtags.</Eintrag>
            </ul>
          </div>

          <div className="gm-legende-block">
            <h3>Nächster Schritt auf jeder Karte</h3>
            <ul>
              {SCHRITT_ERKLAERT.map(({ art, ton, text }) => {
                const { label, Icon } = SCHRITT_INFO[art];
                return (
                  <Eintrag key={art} muster={<span className={`pl-schritt pl-schritt--muster pl-schritt--${ton}`}><span className="pl-schritt-art"><Icon size={13} aria-hidden="true" />{label}</span></span>} titel="">
                    {text}
                  </Eintrag>
                );
              })}
            </ul>
          </div>

          <div className="gm-legende-block">
            <h3>Karte und Gruppen</h3>
            <ul>
              <Eintrag muster={<span className="gm-legende-karte gm-legende-karte--neu" aria-hidden="true"><span className="gm-neu-punkt" /></span>} titel="Roter Streifen links und roter Punkt:">
                Neue Rückmeldungen, die noch niemand aus dem Team gelesen hat. Daneben steht „1 neu“.
              </Eintrag>
              <Eintrag
                muster={(
                  <span className="gm-legende-rueck" aria-hidden="true">
                    <span className="gm-kal-rueck gm-kal-rueck--neu"><MessageSquare size={10} />2</span>
                    <span className="gm-kal-rueck gm-kal-rueck--offen"><MessageSquare size={10} />1</span>
                  </span>
                )}
                titel="Sprechblase mit Zahl im Kalender:"
              >
                Zu dieser Mail liegt Rückmeldung vor — rot: neu und noch ungelesen, orange: offen und noch einzuarbeiten. Die Kachel trägt dazu einen Ring in derselben Farbe.
              </Eintrag>
              <Eintrag muster={<span className="gm-legende-karte gm-legende-karte--aus" aria-hidden="true" />} titel="Blasse Karte, gestrichelter Rahmen:">
                Ausgeblendet (z. B. Testmail) — steht nur da, wenn „Ausgeblendete zeigen“ an ist.
              </Eintrag>
              <Eintrag muster={<span className="gm-legende-gruppe" aria-hidden="true">Termin verstrichen</span>} titel="Orange Überschrift:">
                Der Versandtag ist vorbei, die Mail aber nicht versendet — meist ist sie in Brevo angehalten.
              </Eintrag>
            </ul>
          </div>

          <div className="gm-legende-block">
            <h3>Stand der Abstimmung (oben rechts)</h3>
            <ul>
              {STAENDE.map((s) => (
                <Eintrag key={s} muster={<StandBadge stand={s} />} titel="">{STAND_INFO[s].text}</Eintrag>
              ))}
            </ul>
            <p className="gm-legende-fuss">Der Stand wechselt von selbst, wenn jemand gefragt wird oder antwortet; von Hand unter „Termine“.</p>
          </div>

          <div className="gm-legende-block">
            <h3>Etiketten und Antworten</h3>
            <ul>
              <Eintrag muster={<span className="pl-chip pl-chip--warn"><Users size={13} aria-hidden="true" /><span className="pl-chip-text">Keine Empfänger</span></span>} titel="Orange Etikett:">
                Achtung — keine Empfänger, Rückmeldefrist vorbei oder in Brevo angehalten.
              </Eintrag>
              <Eintrag muster={<span className="pl-chip"><Send size={13} aria-hidden="true" /><span className="pl-chip-text">Do 08.10.</span><span className="pl-chip-zusatz">laut Name</span></span>} titel="Zusatz am Versandtag:">
                Woher der Tag kommt: „geplant“ (eingetragen), „Wunsch“ (Assistent), „laut Name“ (Mailname), „angehalten“ (Brevo).
              </Eintrag>
              <Eintrag muster={<span className="pl-chip"><Mail size={13} aria-hidden="true" /><span className="pl-chip-text">Entwurf in Brevo</span><span className="pl-chip-zusatz pl-chip-zusatz--warn">nicht aktuell</span></span>} titel="„nicht aktuell“:">
                Im Mailtool gibt es Änderungen, die noch nicht in Brevo sind.
              </Eintrag>
              <Eintrag
                muster={(
                  <span className="pl-stapel pl-stapel--marken" aria-hidden="true">
                    <Avatar name="Anna Beispiel" groesse="xs" antwort="passt" />
                    <Avatar name="Ben Muster" groesse="xs" antwort="kommentiert" />
                    <Avatar name="Carla Probe" groesse="xs" antwort="offen" />
                  </span>
                )}
                titel="Gefragte Personen:"
              >
                Haken = „Passt so“, Sprechblase = hat kommentiert, Uhr = Antwort steht noch aus. Daneben z. B. „1 von 3 Antworten offen“; Zeigen oder Antippen nennt alle Namen.
              </Eintrag>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
